import { isIsometricExercise } from '../utils/ExerciseUtils';

/**
 * Analizza la distribuzione del carico e il suo impatto sulla fatica muscolare
 * @param {Array} sets - Array di serie
 * @returns {Object} Analisi della distribuzione del carico
 */
export function analyzeLoadDistribution(sets) {
  // Raggruppa i set per muscolo, tipo di esercizio e range di ripetizioni
  const muscleGroupData = {};
  const exerciseTypeData = {};
  const repRangeData = {
    low: 0,     // 1-5 ripetizioni
    medium: 0,  // 6-12 ripetizioni
    high: 0     // 13+ ripetizioni
  };
  
  // Calcola il volume per ogni categoria
  sets.forEach(set => {
    const muscleGroup = set.gruppo_muscolare || 'Altro';
    const exerciseType = categorizeExerciseType(set.esercizio_nome);
    const reps = parseInt(set.ripetizioni) || 0;
    const weight = parseFloat(set.peso) || 0;
    const volume = weight * reps;
    
    // Aggiorna dati per gruppo muscolare
    if (!muscleGroupData[muscleGroup]) {
      muscleGroupData[muscleGroup] = {
        totalVolume: 0,
        setCount: 0,
        averageWeight: 0,
        estimatedFatigue: 0
      };
    }
    muscleGroupData[muscleGroup].totalVolume += volume;
    muscleGroupData[muscleGroup].setCount++;
    muscleGroupData[muscleGroup].averageWeight += weight;
    
    // Aggiorna dati per tipo di esercizio
    if (!exerciseTypeData[exerciseType]) {
      exerciseTypeData[exerciseType] = {
        totalVolume: 0,
        setCount: 0
      };
    }
    exerciseTypeData[exerciseType].totalVolume += volume;
    exerciseTypeData[exerciseType].setCount++;
    
    // Aggiorna dati per range di ripetizioni
    if (reps <= 5) {
      repRangeData.low += volume;
    } else if (reps <= 12) {
      repRangeData.medium += volume;
    } else {
      repRangeData.high += volume;
    }
  });
  
  // Calcola medie per gruppo muscolare
  for (const [group, data] of Object.entries(muscleGroupData)) {
    if (data.setCount > 0) {
      data.averageWeight = data.averageWeight / data.setCount;
    }
    
    // Stima dell'affaticamento muscolare
    const setCount = data.setCount;
    const volumeFactor = data.totalVolume / 1000; // normalizzato
    
    // Scala 1-10 di affaticamento stimato
    data.estimatedFatigue = Math.min(10, Math.round(
      (setCount * 0.5) + // Fattore numero di set
      (volumeFactor * 2) // Fattore volume
    ));
  }
  
  // Normalizza i dati del tipo di esercizio
  const totalVolume = Object.values(exerciseTypeData).reduce((sum, data) => sum + data.totalVolume, 0);
  for (const data of Object.values(exerciseTypeData)) {
    data.percentageOfTotal = totalVolume > 0 ? (data.totalVolume / totalVolume * 100).toFixed(1) : 0;
  }
  
  // Normalizza i dati del range di ripetizioni
  const totalRepVolume = repRangeData.low + repRangeData.medium + repRangeData.high;
  if (totalRepVolume > 0) {
    repRangeData.lowPercent = (repRangeData.low / totalRepVolume * 100).toFixed(1);
    repRangeData.mediumPercent = (repRangeData.medium / totalRepVolume * 100).toFixed(1);
    repRangeData.highPercent = (repRangeData.high / totalRepVolume * 100).toFixed(1);
  }
  
  // Genera analisi completa
  return {
    muscleGroups: Object.entries(muscleGroupData)
      .map(([group, data]) => ({ name: group, ...data }))
      .sort((a, b) => b.totalVolume - a.totalVolume),
    exerciseTypes: exerciseTypeData,
    repRanges: repRangeData,
    totalVolume: totalVolume,
    fatigueIndex: calculateOverallFatigueIndex(muscleGroupData),
    recommendations: generateLoadDistributionRecommendations(
      muscleGroupData, 
      exerciseTypeData, 
      repRangeData
    )
  };
}

/**
 * Calcola un indice di affaticamento complessivo
 * @param {Object} muscleGroupData - Dati sui gruppi muscolari
 * @returns {number} Indice di affaticamento (1-10)
 */
function calculateOverallFatigueIndex(muscleGroupData) {
  const muscleGroups = Object.values(muscleGroupData);
  if (muscleGroups.length === 0) return 0;
  
  // Usa la media ponderata dei valori di affaticamento
  const totalSets = muscleGroups.reduce((sum, data) => sum + data.setCount, 0);
  const weightedFatigue = muscleGroups.reduce((sum, data) => {
    return sum + (data.estimatedFatigue * data.setCount);
  }, 0);
  
  return totalSets > 0 ? Math.round(weightedFatigue / totalSets) : 0;
}

/**
 * Categorizza il tipo di esercizio
 * @param {string} exerciseName - Nome dell'esercizio
 * @returns {string} Categoria dell'esercizio
 */
function categorizeExerciseType(exerciseName) {
  if (!exerciseName) return 'Altro';
  
  const name = exerciseName.toLowerCase();
  
  const compoundExercises = ['squat', 'stacco', 'panca', 'military', 'rematore', 'trazione', 'leg press', 'dip'];
  const isolationExercises = ['curl', 'extension', 'alzate', 'croci', 'fly', 'kick', 'calf'];
  const machineExercises = ['macchina', 'machine', 'cable', 'cavi', 'pulley', 'press', 'hammer'];
  
  if (compoundExercises.some(ex => name.includes(ex))) {
    return 'Esercizi Composti';
  } else if (isolationExercises.some(ex => name.includes(ex))) {
    return 'Esercizi Isolamento';
  } else if (machineExercises.some(ex => name.includes(ex))) {
    return 'Macchine/Cavi';
  } else {
    return 'Altro';
  }
}

/**
 * Genera raccomandazioni basate sull'analisi della distribuzione del carico
 * @param {Object} muscleGroupData - Dati sui gruppi muscolari
 * @param {Object} exerciseTypeData - Dati sui tipi di esercizio
 * @param {Object} repRangeData - Dati sui range di ripetizioni
 * @returns {Array} Raccomandazioni
 */
function generateLoadDistributionRecommendations(muscleGroupData, exerciseTypeData, repRangeData) {
  const recommendations = [];
  
  // Identifica squilibri muscolari
  const muscleGroups = Object.entries(muscleGroupData);
  if (muscleGroups.length >= 2) {
    // Ordina per volume totale
    muscleGroups.sort((a, b) => b[1].totalVolume - a[1].totalVolume);
    
    const highestVolumeGroup = muscleGroups[0];
    const lowestVolumeGroup = muscleGroups[muscleGroups.length - 1];
    
    // Se c'è un grande divario tra i gruppi (più di 3x)
    if (highestVolumeGroup[1].totalVolume > lowestVolumeGroup[1].totalVolume * 3) {
      recommendations.push({
        title: "Bilanciamento Muscolare",
        description: `Il volume per ${highestVolumeGroup[0]} è significativamente più alto di ${lowestVolumeGroup[0]}. Considera di bilanciare meglio i gruppi muscolari.`,
        priority: "medium",
        actionable: true, // Aggiunta flag per indicare se il suggerimento è direttamente applicabile
        suggestedAction: `Aggiungi 2-3 serie aggiuntive per ${lowestVolumeGroup[0]} nei prossimi allenamenti`
      });
    }
  }
  
  // Analisi più dettagliata del tipo di esercizio con suggerimenti migliori
  const compoundPercentage = exerciseTypeData['Esercizi Composti']?.percentageOfTotal || 0;
  if (compoundPercentage < 40) {
    recommendations.push({
      title: "Distribuzione Tipo Esercizi",
      description: "La percentuale di esercizi composti è bassa. Considera di includere più squat, stacchi, panca o trazioni per una crescita più efficace.",
      priority: "medium",
      actionable: true,
      suggestedAction: "Aggiungi almeno un esercizio composto all'inizio di ogni sessione di allenamento"
    });
  } else if (compoundPercentage > 80) {
    // Nuovo: suggerimento per bilanciare con esercizi di isolamento
    recommendations.push({
      title: "Integra Esercizi di Isolamento",
      description: "La tua scheda è molto concentrata su esercizi composti (>80%). Integrare esercizi di isolamento può migliorare lo sviluppo muscolare e prevenire squilibri.",
      priority: "low",
      actionable: true,
      suggestedAction: "Aggiungi 2-3 esercizi di isolamento per lavorare su muscoli specifici"
    });
  }
  
  // Analisi distribuzione ripetizioni migliorata
  if (parseFloat(repRangeData.lowPercent) > 80) {
    recommendations.push({
      title: "Range di Ripetizioni per Ipertrofia",
      description: "Ti stai concentrando molto su ripetizioni basse (1-5). Considera di includere alcune serie nel range 8-12 per bilanciare forza e ipertrofia.",
      priority: "low",
      actionable: true,
      suggestedAction: "Per 2-3 esercizi, fai 3 serie da 8-12 ripetizioni con peso moderato"
    });
  } else if (parseFloat(repRangeData.highPercent) > 70) {
    recommendations.push({
      title: "Range di Ripetizioni per Forza",
      description: "Ti stai concentrando molto su ripetizioni alte (13+). Considera di includere alcune serie nel range 3-6 o 6-8 per stimolare guadagni di forza.",
      priority: "low",
      actionable: true,
      suggestedAction: "Per i tuoi esercizi composti principali, aggiungi 2-3 serie da 4-6 ripetizioni con peso più alto"
    });
  } else if (parseFloat(repRangeData.mediumPercent) > 90) {
    // Nuovo: suggerimento per variare il range di ripetizioni
    recommendations.push({
      title: "Varia il Range di Ripetizioni",
      description: "Ti alleni quasi esclusivamente nel range medio (6-12). Variare il range di ripetizioni può ottimizzare i guadagni muscolari e di forza.",
      priority: "low",
      actionable: true,
      suggestedAction: "Alterna: settimana 1 con 4-6 ripetizioni pesanti, settimana 2 con 8-12 ripetizioni moderate"
    });
  }
  
  // Analisi affaticamento
  const highlyFatiguedGroups = Object.entries(muscleGroupData)
    .filter(([_, data]) => data.estimatedFatigue >= 8)
    .map(([group, _]) => group);
  
  if (highlyFatiguedGroups.length > 0) {
    recommendations.push({
      title: "Potenziale Sovrallenamento",
      description: `${highlyFatiguedGroups.join(', ')} mostrano segnali di alto affaticamento. Considera di inserire giorni di recupero prima di riallenarli.`,
      priority: "high",
      actionable: true,
      suggestedAction: `Evita di allenare ${highlyFatiguedGroups.join(', ')} per almeno 48-72 ore`
    });
  }
  
  return recommendations;
}